import { captureMessage } from '@sentry/react';
import { find, uniq, max, get, round, multiply } from 'lodash';
import { CardData, StorySettingsType } from 'types/story';
import { SCORE } from 'common/constants';
import { CardFacade } from 'common/utils/facades/card-facade';
import { clientLog, int } from 'common/utils/helpers';
import { lsNames } from 'client/constants/common';
import produce from 'immer';

const log = clientLog.extend('StoryHistory');

type StoryId = string;
type CardId = string;
type StoredDataT = Record<
	StoryId,
	{
		// current play(cp). temporary. initialized on every page load.
		cp?: { cards: Record<CardId, { answers?: string[] /* answer id arr */ }> };
		// last visited card(lvc). permanent. do not delete it.
		lvc?: CardId;
	}
>;

const getDefaultStoryData = (): StoredDataT[StoryId] => ({
	cp: { cards: {} },
});

export class StoryHistory {
	storyId: string;

	itemName = lsNames.history;

	constructor({ storyId = '' }) {
		this.storyId = storyId;
	}

	get history(): StoredDataT {
		const item = localStorage.getItem(this.itemName);
		const itemParsed: StoredDataT = JSON.parse(item || JSON.stringify({}));

		if (!itemParsed[this.storyId]) {
			itemParsed[this.storyId] = getDefaultStoryData();
		}

		if (!itemParsed[this.storyId]?.cp) {
			itemParsed[this.storyId].cp = getDefaultStoryData().cp;
		}

		return itemParsed;
	}

	get lastVisitedCard(): string | undefined {
		const story = this.history[this.storyId];
		return story.lvc ?? (story as any).cardId /* right operand is an old syntax <= release v1.11.7 */;
	}

	get cardsPlayed() {
		return Object.entries(this.history[this.storyId].cp?.cards ?? []);
	}

	isCardVisited({ cardId }: { cardId: string }) {
		return this.history[this.storyId].cp?.cards[cardId] !== undefined;
	}

	clearLastPlay() {
		localStorage.setItem(
			this.itemName,
			JSON.stringify(
				produce(this.history, draft => {
					new Set([...Object.keys(draft), this.storyId]).forEach(storyId => {
						if (storyId === this.storyId) draft[storyId].cp = getDefaultStoryData().cp;
						else delete draft[storyId].cp;
					});
				})
			)
		);
	}

	addCard({ cardId }: { cardId: string }) {
		localStorage.setItem(
			this.itemName,
			JSON.stringify(
				produce(this.history, draft => {
					draft[this.storyId].lvc = cardId;
					draft[this.storyId].cp!.cards[cardId] = {};
				})
			)
		);
	}

	addAnswer({ answerId, cardId, answersMax = 1 }: { answerId: string; cardId: string; answersMax?: number }) {
		const { history } = this;
		const prevAnswers = history[this.storyId].cp!.cards[cardId]?.answers ?? [];
		const answers = answersMax > 1 ? uniq([...prevAnswers, answerId]) : [answerId];

		localStorage.setItem(
			this.itemName,
			JSON.stringify(
				produce(history, draft => {
					try {
						draft[this.storyId].cp!.cards[cardId].answers = answers;
					} catch (e) {
						captureMessage(`[DEBUG] StoryHistory.addAnswer(): ${(e as Error).message}`, {
							level: 'error',
							contexts: {
								data: {
									storyId: this.storyId,
									cardId,
									history: JSON.stringify(history[this.storyId]),
								},
							},
						});
					}
				})
			)
		);
	}

	/**
	 * Get total score for played cards
	 */
	totalScore(storySettings: StorySettingsType, cards: CardData[]) {
		const result = {
			scoreMax: 0, // Maximum score - the sum of the maximum score for the cards played
			scoreTotal: 0, // Total score - the amount of score received for the cards played
			scoreRelative: 0, // Relative score - the ratio of the total score to the maximum (0 - 100)
			answersTotal: 0,
			answersCorrect: 0,
		};

		this.cardsPlayed.forEach(([cardId, { answers: chosenAnswerIds = [] }]) => {
			const { score, answers = {} } = storySettings.cards?.[cardId] ?? {};
			const cardData = find(cards, ['_id', cardId]);
			const canScore = cardData ? CardFacade.hasFeature(cardData.type).score : false;
			const defaultCardScore = canScore ? SCORE.DEFAULT : 0;

			// Get card score
			const cardScore = int(score ?? defaultCardScore);
			if (cardScore) result.scoreMax += cardScore;

			// Get max answer score per card (e.g. PersonalityTest card)
			const defaultAnswerScore = cardData && CardFacade.hasFeature(cardData.type).answerScore ? SCORE.DEFAULT : 0;
			const answerMaxScore = max(
				Object.values(answers).map(answer => int('score' in answer ? answer.score : defaultAnswerScore))
			);
			if (answerMaxScore) result.scoreMax += answerMaxScore;

			/* count answers. each visited card that can score points must increase the "answersTotal" value.
			   If the user did not answer, it will be considered that he answered incorrectly. */
			if (canScore) result.answersTotal += 1;

			// sum up the score for chosen answers
			chosenAnswerIds.forEach(answerId => {
				// Add card score for a correct answer (eg. Trivia card)
				// TODO: if answersMax > 1 score should be incremented partially in depends on count of correct answers
				const isCorrect = get(answers, `${answerId}.isCorrect`);
				result.scoreTotal += isCorrect ? cardScore : 0;

				// inc counters
				if (canScore) if (isCorrect) result.answersCorrect += 1;

				// Add answer score (PersonalityTest card)
				const answerScore = int(get(answers, [answerId, 'score']) ?? defaultAnswerScore);
				if (answerScore) result.scoreTotal += answerScore;
			});
		});

		result.scoreRelative = multiply(result.scoreTotal / result.scoreMax || 0, 100);

		log('totalScore', result);

		return result;
	}

	/**
	 * Get total character points for played cards
	 */
	totalCharacterPoints(storySettings: StorySettingsType) {
		const result: { [characterId: string]: number } = {};

		this.cardsPlayed.forEach(([cardId, data]) => {
			const playedCardAnswerSettings = storySettings.cards?.[cardId]?.answers;
			const playedCardChosenAnswerIds = data.answers;

			if (!playedCardAnswerSettings) return;

			playedCardChosenAnswerIds?.forEach(playedAnswerId => {
				const answerSettings = playedCardAnswerSettings[playedAnswerId];

				if (answerSettings && 'characterPoints' in answerSettings && answerSettings.characterPoints) {
					Object.entries(answerSettings.characterPoints).forEach(([characterId, characterPoints]) => {
						const characterPointsNumber = parseFloat(characterPoints);

						if (Number.isNaN(characterPointsNumber)) return;

						result[characterId] =
							result[characterId] === undefined
								? characterPointsNumber
								: result[characterId] + characterPointsNumber;
					});
				}
			});
		});

		log('totalCharacterPoints', result);

		return result;
	}
}

class _AnswerDelta {
	private delta: Record<string, number> = {};

	start(cardId: string) {
		this.delta[cardId] = Date.now();

		log('_AnswerDelta start', this.delta[cardId]);
	}

	end(cardId: string) {
		if (!this.delta[cardId]) return;

		this.delta[cardId] = round((Date.now() - this.delta[cardId]) / 1000, 3);

		log('_AnswerDelta end', this.delta[cardId]);
	}

	get(cardId: string) {
		return this.delta[cardId];
	}
}

export const AnswerDelta = new _AnswerDelta();
